import { createContext, useReducer } from "react";
import {
	CHARACTERISTICS_OF_RESPONDENTS_AND_HOUSEHOLDS,
	DAILY_DISASTER_PREPARATION,
	LOSSES_FROM_THE_CHISHANG_EARTHQUAKE,
	WHAT_PEOPLE_DID_AFTER_THE_CHISHANG_EARTHQUAKE,
} from "../constants";

/**
 * @typedef {Object} State
 * @property {string} search The current search query.
 * @property {Set} filter The current filter state.
 */

/**
 * @typedef {Object} Action
 * @property {string} type - Type of action to be processed.
 * @property {string|Set} payload - Data to be used for state update.
 */

/**
 * @function reducer
 * Reducer function to manage state transitions based on action types.
 *
 * @param {State} state - Current state of the component.
 * @param {Action} action - Action object containing type and payload.
 *
 * @returns {State} New state after applying the action.
 *
 * @throws Will throw an error if the action type is unknown.
 */

const reducer = (state, action) => {
	switch (action.type) {
		case "set_search":
			return {
				...state,
				search: action.payload,
			};
		case "apply_filter":
			return {
				...state,
				filter: action.payload,
			};
		default:
			throw Error(`Unknown action: ${action.type}`);
	}
};

/**
 * @typedef {Object} ChartDataContextValue
 * @property {State} state: The current state including `search` and `filter`.
 * @property {Function} setSearch: Function to update the search query.
 * @property {Function} toggleThemeFilter: Function to toggle the state of a theme filter.
 */

/**
 * @function useChartDataQuery
 * Custom hook to manage chart data queries with search and filter capabilities.
 *
 * This hook provides state management for search queries and theme filters
 * related to chart data. It utilizes a reducer to handle state transitions
 * based on dispatched actions. The initial state includes an empty search
 * string and a predefined set of theme filters.
 *
 * @returns {ChartDataContextValue} An object containing:
 * - {State} state: The current state including `search` and `filter`.
 * - {Function} setSearch: Function to update the search query.
 * - {Function} toggleThemeFilter: Function to toggle the state of a theme filter.
 */

const useChartDataQuery = () => {
	const initThemeFilter = new Set([
		DAILY_DISASTER_PREPARATION,
		WHAT_PEOPLE_DID_AFTER_THE_CHISHANG_EARTHQUAKE,
		LOSSES_FROM_THE_CHISHANG_EARTHQUAKE,
		CHARACTERISTICS_OF_RESPONDENTS_AND_HOUSEHOLDS,
	]);
	const [state, dispatch] = useReducer(reducer, {
		search: "",
		filter: initThemeFilter,
	});

	/**
	 * @function toggleThemeFilter
	 * Toggles the state of a theme filter.
	 *
	 * If the filter item is "所有圖表", it will toggle the state of all filters.
	 * Otherwise, it will toggle the state of the corresponding filter.
	 *
	 * @param {string} filterItem The filter item to be toggled.
	 */
	const toggleThemeFilter = (filterItem) => {
		let newFilter = new Set(state.filter);

		if (filterItem === "所有圖表") {
			if (newFilter.size === 4) return;
			newFilter = initThemeFilter;
		} else {
			if (newFilter.size === 4) newFilter.clear();
			newFilter.has(filterItem)
				? newFilter.delete(filterItem)
				: newFilter.add(filterItem);
			if (newFilter.size === 0) newFilter = initThemeFilter;
		}

		dispatch({
			type: "apply_filter",
			payload: newFilter,
		});
	};

	/**
	 * @function setSearch
	 * @param {string} search - The search query string.
	 */
	const setSearch = (search) => {
		dispatch({
			type: "set_search",
			payload: search,
		});
	};

	return { query: state, setSearch, toggleThemeFilter };
};

/**
 * Context to provide chart data throughout the component tree.
 *
 * @type {React.Context<ChartDataContextValue>}
 */
export const ChartDataContext = createContext(null);

export default useChartDataQuery;
